/**
 * Memory monitoring utilities for development
 * Helps detect memory leaks and performance issues
 */

interface MemorySnapshot {
  timestamp: number;
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  percentage: number;
}

interface MemoryStats {
  current: MemorySnapshot;
  snapshots: MemorySnapshot[];
  leakDetected: boolean;
  growthRate: number; // MB per minute
}

class MemoryMonitor {
  private snapshots: MemorySnapshot[] = [];
  private intervalId: number | null = null;
  private readonly SNAPSHOT_INTERVAL = 30000; // 30 seconds
  private readonly MAX_SNAPSHOTS = 100; // Keep last 100 snapshots
  private readonly LEAK_THRESHOLD = 50; // MB growth considered a leak

  /**
   * Start monitoring memory usage
   */
  start(): void {
    if (this.intervalId !== null) {
      console.warn("Memory monitor already running");
      return;
    }

    // Only run in development
    if (process.env.NODE_ENV !== "development") {
      console.warn("Memory monitor only runs in development");
      return;
    }

    // Check if memory API is available
    if (!this.isMemoryAPIAvailable()) {
      console.warn("Memory API not available in this browser");
      return;
    }

    console.log("🔍 Memory monitoring started");
    this.takeSnapshot(); // Initial snapshot

    this.intervalId = window.setInterval(() => {
      this.takeSnapshot();
      this.checkForLeaks();
    }, this.SNAPSHOT_INTERVAL);
  }

  /**
   * Stop monitoring
   */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log("🛑 Memory monitoring stopped");
    }
  }

  /**
   * Take a memory snapshot
   */
  private takeSnapshot(): void {
    if (!this.isMemoryAPIAvailable()) return;

    const memory = (performance as any).memory;
    const snapshot: MemorySnapshot = {
      timestamp: Date.now(),
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      percentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
    };

    this.snapshots.push(snapshot);

    // Keep only recent snapshots
    if (this.snapshots.length > this.MAX_SNAPSHOTS) {
      this.snapshots.shift();
    }
  }

  /**
   * Check for memory leaks
   */
  private checkForLeaks(): void {
    if (this.snapshots.length < 5) return; // Need at least 5 snapshots

    const stats = this.getStats();

    // Log warning if memory is growing rapidly
    if (stats.growthRate > 10) {
      console.warn(
        `⚠️ High memory growth rate: ${stats.growthRate.toFixed(2)} MB/min`,
      );
    }

    // Log error if leak detected
    if (stats.leakDetected) {
      console.error(
        `🚨 Potential memory leak detected! Growth: ${stats.growthRate.toFixed(2)} MB/min`,
      );
      console.error(
        `Current usage: ${this.formatBytes(stats.current.usedJSHeapSize)}`,
      );
    }

    // Log current usage periodically
    if (this.snapshots.length % 10 === 0) {
      console.log(
        `💾 Memory: ${this.formatBytes(stats.current.usedJSHeapSize)} (${stats.current.percentage.toFixed(1)}%)`,
      );
    }
  }

  /**
   * Get memory statistics
   */
  getStats(): MemoryStats {
    if (this.snapshots.length === 0) {
      throw new Error("No snapshots available");
    }

    const current = this.snapshots[this.snapshots.length - 1];
    const growthRate = this.calculateGrowthRate();
    const leakDetected = growthRate > this.LEAK_THRESHOLD;

    if (!current) {
      throw new Error("No current snapshot available");
    }

    return {
      current,
      snapshots: [...this.snapshots],
      leakDetected,
      growthRate,
    };
  }

  /**
   * Calculate memory growth rate in MB per minute
   */
  private calculateGrowthRate(): number {
    if (this.snapshots.length < 2) return 0;

    const oldest = this.snapshots[0];
    const newest = this.snapshots[this.snapshots.length - 1];

    if (!newest || !oldest) {
      throw new Error("No snapshots available");
    }

    const timeDiffMinutes = (newest.timestamp - oldest.timestamp) / 1000 / 60;
    const memoryDiffMB =
      (newest.usedJSHeapSize - oldest.usedJSHeapSize) / 1024 / 1024;

    return memoryDiffMB / timeDiffMinutes;
  }

  /**
   * Format bytes to human-readable string
   */
  private formatBytes(bytes: number): string {
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(2)} MB`;
  }

  /**
   * Check if memory API is available
   */
  private isMemoryAPIAvailable(): boolean {
    return (
      typeof performance !== "undefined" &&
      "memory" in performance &&
      (performance as any).memory !== undefined
    );
  }

  /**
   * Export snapshots for analysis
   */
  exportSnapshots(): string {
    return JSON.stringify(this.snapshots, null, 2);
  }

  /**
   * Clear all snapshots
   */
  clear(): void {
    this.snapshots = [];
    console.log("📭 Memory snapshots cleared");
  }

  /**
   * Get current memory usage
   */
  getCurrentUsage(): MemorySnapshot | null {
    if (!this.isMemoryAPIAvailable()) return null;

    const memory = (performance as any).memory;
    return {
      timestamp: Date.now(),
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      percentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
    };
  }
}

// Singleton instance
const memoryMonitor = new MemoryMonitor();

// Auto-start in development if enabled
if (
  typeof window !== "undefined" &&
  process.env.NODE_ENV === "development" &&
  process.env.NEXT_PUBLIC_ENABLE_MEMORY_MONITOR === "true"
) {
  memoryMonitor.start();

  // Add to window for debugging
  (window as any).__memoryMonitor = memoryMonitor;
}

export { memoryMonitor, type MemorySnapshot, type MemoryStats };
